/*
 * The admin auth context: the one place the console's React tree learns whether an operator is
 * signed in, who they are, and whether they still owe a forced reset. The raw token lives in the
 * non-React session store (sessionStorage); this provider holds only the decoded claims in state and
 * exposes `getToken()` for the API client. Sign-in persists the token and decodes it; sign-out
 * clears it (logout is a client-side drop — the JWT is stateless). Initial state is seeded from the
 * store so a page reload within the tab stays signed in.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createAdminSessionStore, type AdminSessionStore } from './admin-session-store';
import { decodeAdminClaims, type AdminClaims } from './admin-token';

export interface AdminAuth {
  /** The decoded claims of the signed-in operator, or null when signed out. */
  readonly claims: AdminClaims | null;
  /** Persist and adopt a freshly issued admin token (login or reset). */
  readonly signIn: (token: string) => void;
  /** Drop the token and return to a signed-out state (logout). */
  readonly signOut: () => void;
  /** The raw token for attaching to admin API calls, or null when signed out. */
  readonly getToken: () => string | null;
}

const AdminAuthContext = createContext<AdminAuth | null>(null);

/** The admin auth context. Throws if used outside an `<AdminAuthProvider>`. */
export function useAdminAuth(): AdminAuth {
  const auth = useContext(AdminAuthContext);
  if (auth === null) {
    throw new Error('useAdminAuth must be used inside an <AdminAuthProvider>');
  }
  return auth;
}

export interface AdminAuthProviderProps {
  readonly children: ReactNode;
  /** Injected for tests; defaults to the sessionStorage-backed store. */
  readonly store?: AdminSessionStore;
}

export function AdminAuthProvider({ children, store }: AdminAuthProviderProps) {
  const storeRef = useRef<AdminSessionStore | null>(store ?? null);
  storeRef.current ??= createAdminSessionStore();
  const sessionStore = storeRef.current;

  const [claims, setClaims] = useState<AdminClaims | null>(() => {
    const existing = sessionStore.read();
    return existing === null ? null : decodeAdminClaims(existing);
  });

  const signIn = useCallback(
    (token: string) => {
      sessionStore.write(token);
      setClaims(decodeAdminClaims(token));
    },
    [sessionStore],
  );

  const signOut = useCallback(() => {
    sessionStore.clear();
    setClaims(null);
  }, [sessionStore]);

  const getToken = useCallback(() => sessionStore.read(), [sessionStore]);

  const value = useMemo<AdminAuth>(
    () => ({ claims, signIn, signOut, getToken }),
    [claims, signIn, signOut, getToken],
  );

  return <AdminAuthContext value={value}>{children}</AdminAuthContext>;
}
